import { useEffect } from "react";
import OklahomaCityHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const stickyNavLinks = [
  { label: "Processing", href: "#processing" },
  { label: "Services", href: "#services" },
  { label: "Local Area", href: "#local-area" },
  { label: "Timing", href: "#timing" },
  { label: "Destinations", href: "#destinations" },
  { label: "FAQ", href: "#faq" },
  { label: "Compare", href: "#compare" },
  { label: "Mistakes", href: "#mistakes" },
  { label: "How It Works", href: "#how-it-works" },
];

const faqs = [
  { q: "Can I get an emergency passport in Oklahoma City if I have a flight in 48 hours?", a: "Yes. For Oklahoma City residents with imminent international travel, we offer emergency passport processing as fast as 24 hours. You will need to visit an acceptance facility first for identity verification, then we hand-carry your documents to the State Department. Call 877-253-0084 immediately to confirm your travel date and eligibility." },
  { q: "Where is the nearest passport acceptance facility in Oklahoma City?", a: "The main Oklahoma City Metro Passport Office at 400 NW 27th Street accepts passport applications. The Oklahoma City Downtown Post Office on 430 W Main Street also offers acceptance services. Tinker AFB has a military passport office for active duty and dependents. We provide detailed instructions for your nearest facility." },
  { q: "How does Oklahoma City expedited passport processing compare to routine service?", a: "Routine passport service through the post office takes 10–13 weeks. The State Department's expedited service takes 7–9 weeks but still requires mailing. Our courier service delivers to the State Department directly, cutting processing to 24 hours to 2 weeks depending on urgency. Most Oklahoma City residents choose 3–5 business day service for a balance of speed and cost." },
  { q: "Do I need to visit an acceptance facility before using your service?", a: "Yes. First-time applicants, minors, and those replacing lost or damaged passports must visit an acceptance facility for identity verification. Oklahoma City residents can visit the Metro Passport Office or any authorized post office. We provide step-by-step instructions and a pre-filled checklist to make your visit quick." },
  { q: "Can I renew my passport without visiting an acceptance facility?", a: "Yes. If your most recent passport was issued when you were 16 or older, is undamaged, and was issued within the last 15 years, you can renew by mail without visiting an acceptance facility. We handle the entire mailing process and deliver to the State Department directly for faster processing." },
  { q: "What documents do I need for a child passport in Oklahoma City?", a: "For children under 16, you need the child's birth certificate, both parents' valid IDs, a DS-11 form, passport photos, and proof of parental relationship. Both parents must appear together, or the appearing parent must submit a notarized DS-3053 consent form. We provide detailed guidance for Oklahoma City families." },
  { q: "How much does expedited passport service cost for Oklahoma City residents?", a: "Our expedited service fees range from $149 for 2-week service to $499 for 24-hour emergency processing. This includes our courier fee, State Department fees are paid separately. Compared to FedEx and Staples resellers, our rates are typically 30–100% lower. Contact us for a custom quote based on your timeline." },
  { q: "Can Tinker AFB families get faster passport processing?", a: "Yes. Military families with PCS orders or emergency deployment travel receive priority handling. Tinker AFB has an on-base passport office for active duty. We work with military families throughout the Oklahoma City area, providing fast processing for both CONUS and OCONUS moves. Bring your orders for verification." },
];

const services = [
  { icon: "ri-passport-line", title: "New Passport", desc: "First-time adult and minor passport applications with complete acceptance clerk guidance." },
  { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for adults with passports issued within the last 15 years at age 16 or older." },
  { icon: "ri-alarm-warning-line", title: "Lost / Stolen", desc: "Complete replacement service including DS-64 form filing and police report guidance if needed." },
  { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance, dual-parent requirements, and photo compliance." },
  { icon: "ri-exchange-dollar-line", title: "Name Change", desc: "Marriage, divorce, and legal name change corrections with court document verification." },
  { icon: "ri-calendar-todo-line", title: "Second Passport", desc: "Second valid passport for frequent travelers and business professionals needing visa flexibility." },
  { icon: "ri-hourglass-line", title: "Emergency 24hr", desc: "Life-or-death emergency and same-day departure processing with hand-carry delivery." },
  { icon: "ri-file-damage-line", title: "Damaged Passport", desc: "Mutilated, water-damaged, or torn passport replacement with damage assessment guidance." },
];

const testimonials = [
  { name: "Marcus T.", location: "Midwest City, OK", text: "Got my passport in 3 days for a business trip to Tokyo. The team walked me through the Tinker AFB acceptance facility process and made everything simple." },
  { name: "Jennifer R.", location: "Edmond, OK", text: "Our family's cruise was in 10 days and my daughter's passport was expired. Fast Passport Center got her renewed passport in 5 days. Saved our vacation!" },
  { name: "Dr. Samira K.", location: "OU Health, OKC", text: "Needed my passport for a medical conference in Geneva. The 2-week service was perfect and cost half what FedEx quoted. Highly professional." },
];

const facilities = [
  { name: "Oklahoma City Metro Passport Office", address: "400 NW 27th Street, Oklahoma City, OK 73118", hours: "Mon–Fri: 8:00 AM – 4:00 PM", phone: "(405) 555-0100", notes: "No appointments. Walk-in only. Photo services available." },
  { name: "Oklahoma City Downtown Post Office", address: "430 W Main Street, Oklahoma City, OK 73102", hours: "Mon–Fri: 9:00 AM – 5:00 PM, Sat: 9:00 AM – 12:00 PM", phone: "(405) 555-0200", notes: "Appointment recommended. Call ahead." },
  { name: "Tinker AFB Passport Office", address: "Bldg 3901, Tinker AFB, OK 73145", hours: "Mon–Fri: 8:00 AM – 3:00 PM", phone: "(405) 555-0300", notes: "Active duty and dependents only. CAC required." },
  { name: "Midwest City Post Office", address: "8300 E Reno Ave, Midwest City, OK 73110", hours: "Mon–Fri: 9:00 AM – 5:00 PM", phone: "(405) 555-0400", notes: "Passport acceptance by appointment." },
];

const destinations = [
  { name: "Cancun", country: "Mexico", flag: "🇲🇽", desc: "Direct flights from Will Rogers World Airport to Cancun take under 3 hours. Popular for spring break, family vacations, and beach getaways." },
  { name: "London", country: "United Kingdom", flag: "🇬🇧", desc: "Oklahoma City business travelers and OU students frequently visit London for conferences, study abroad, and cultural exchange programs." },
  { name: "Tokyo", country: "Japan", flag: "🇯🇵", desc: "Energy industry executives and business professionals from OKC travel to Tokyo for trade shows and corporate meetings. 6-month validity required." },
  { name: "Toronto", country: "Canada", flag: "🇨🇦", desc: "Easy weekend trips from Oklahoma City to Toronto for shopping, sports, and business. Passport book or passport card accepted." },
  { name: "Rome", country: "Italy", flag: "🇮🇹", desc: "Popular destination for OU students studying abroad, mission trips, and Catholic pilgrimage travel from Oklahoma City parishes." },
  { name: "Dubai", country: "UAE", flag: "🇦🇪", desc: "Oklahoma oil and gas professionals frequently travel to Dubai for energy conferences and business development. Visa required for stays over 48 hours." },
];

const timingCards = [
  { title: "Summer Break Rush", months: "May–August", desc: "Peak season for family vacations and student travel. Oklahoma City acceptance facilities see 3x normal volume. Book 8–12 weeks ahead or use expedited service." },
  { title: "Holiday Travel", months: "November–January", desc: "Cruise and ski trip season. Many OKC families discover expired passports before Christmas trips. Book early or use 2-week expedited processing." },
  { title: "Spring Break", months: "March–April", desc: "OU, OCCC, and UCO students travel to Cancun, Cabo, and Caribbean destinations. Last-minute passport renewals spike in late February." },
  { title: "Tinker PCS Season", months: "Year-round", desc: "Military families receive PCS orders with 30–60 days notice. Fast processing is critical for OCONUS assignments. Bring orders for priority handling." },
];

const comparisonRows = [
  { feature: "Processing Speed", us: "24 hours to 2 weeks", usdps: "7–9 weeks", fedex: "7–10 weeks", staples: "6–8 weeks", local: "4–8 weeks", diff: "We hand-carry to State Dept." },
  { feature: "Price for 2-Week", us: "$149–$249", usdps: "$130 + $60 expedite", fedex: "$299–$499", staples: "$249–$399", local: "$199–$349", diff: "30–100% lower than resellers" },
  { feature: "Office Visit Required", us: "No — mail only", usdps: "No — mail only", fedex: "Yes — in-person", staples: "Yes — in-person", local: "Yes — in-person", diff: "Save time with mail-only service" },
  { feature: "Acceptance Facility Help", us: "Full guidance + checklist", usdps: "None — DIY", fedex: "Limited", staples: "Limited", local: "Limited", diff: "Step-by-step instructions provided" },
  { feature: "Photo Compliance Check", us: "Yes — pre-review", usdps: "No", fedex: "Sometimes", staples: "Sometimes", local: "No", diff: "Avoid rejection for bad photos" },
  { feature: "Military PCS Support", us: "Yes — priority handling", usdps: "No", fedex: "No", staples: "No", local: "No", diff: "Specialized for military families" },
  { feature: "Real-Time Tracking", us: "Yes — email + phone", usdps: "Limited online", fedex: "Yes", staples: "Yes", local: "No", diff: "Personal updates throughout" },
  { feature: "BBB Rating", us: "A+ Rated", usdps: "N/A", fedex: "A+ Rated", staples: "A+ Rated", local: "Varies", diff: "Top-tier consumer protection" },
  { feature: "Registered Courier", us: "Yes — State Dept.", usdps: "N/A", fedex: "No — reseller", staples: "No — reseller", local: "No — reseller", diff: "Direct government relationship" },
];

const mistakes = [
  { title: "Waiting Too Long", desc: "Many Oklahoma City residents wait until 2 weeks before travel to check their passport. At that point, routine service is impossible. Check your expiration date at least 6 months before any international trip." },
  { title: "Wrong Photo Size", desc: "Post offices and drug stores in OKC often take passport photos that don't meet State Department specifications. We pre-review your photo for compliance to avoid rejection delays." },
  { title: "Missing Documents for Minors", desc: "Child passports require both parents' presence or a notarized consent form. Many Oklahoma City families arrive at the acceptance facility missing the DS-3053, causing trip-killing delays." },
  { title: "Not Using the Right Form", desc: "First-time applicants use DS-11. Renewals use DS-82. Using the wrong form can cause weeks of delays. We provide the correct form with pre-filled instructions for your situation." },
  { title: "Ignoring the 6-Month Rule", desc: "Many countries require 6 months of passport validity beyond your travel date. A passport expiring in 4 months may be rejected at the OKC airport check-in or foreign border." },
];

const steps = [
  { num: "1", title: "Call or Apply Online", desc: "Call 877-253-0084 or start your application online. We assess your timeline and recommend the right service level for your Oklahoma City travel needs." },
  { num: "2", title: "Gather Documents", desc: "We send a personalized checklist for your situation — birth certificate, ID, photos, travel proof. We pre-review everything to catch errors before submission." },
  { num: "3", title: "Visit Acceptance Facility", desc: "For first-time and minor applicants, we guide you to the nearest Oklahoma City acceptance facility with step-by-step instructions and a pre-filled application." },
  { num: "4", title: "We Hand-Carry to State Dept.", desc: "As a registered courier, we deliver your documents directly to the State Department. No mailing delays. No middlemen. Direct processing for maximum speed." },
  { num: "5", title: "Receive Your Passport", desc: "Your passport ships back to your Oklahoma City address via secure courier. We track the delivery and confirm receipt with you." },
];

const resources = [
  { title: "Oklahoma City Metro Passport Office", url: "https://travel.state.gov", desc: "Walk-in acceptance facility at 400 NW 27th Street." },
  { title: "Tinker AFB Passport Services", url: "https://www.tinker.af.mil", desc: "Military passport office for active duty and dependents." },
  { title: "Will Rogers World Airport", url: "https://www.flyokc.com", desc: "OKC's international airport with flights to Canada, Mexico, and connecting worldwide." },
  { title: "Oklahoma City Travel Requirements", url: "https://travel.state.gov", desc: "Country-specific entry requirements, visa information, and travel advisories." },
  { title: "OU Study Abroad Programs", url: "https://www.ou.edu/abroad", desc: "University of Oklahoma international education and passport requirements." },
];

export default function OklahomaCityPage() {
  useEffect(() => {
    document.title = "Emergency Passport Oklahoma City, OK — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/oklahoma-city/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Oklahoma City, OK — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving OKC military families, OU students, business travelers, and families.");
    setMeta("geo.placename", "Oklahoma City, Oklahoma");
    setMeta("geo.region", "US-OK");
    setMeta("geo.position", "35.4676;-97.5164");
    setMeta("ICBM", "35.4676, -97.5164");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport", description: "Second passport issuance for frequent travelers and business professionals needing visa flexibility." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Oklahoma", item: "https://fastpassportcenter.com/oklahoma/" },
          { "@type": "ListItem", position: 3, name: "Oklahoma City Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Oklahoma City — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Oklahoma City and Central Oklahoma travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Oklahoma City",
          containedInPlace: {
            "@type": "State",
            name: "Oklahoma",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Oklahoma City",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <OklahomaCityHeroSection />

      {/* Sticky Nav */}
      <div className="sticky top-[70px] z-40 bg-white/95 backdrop-blur border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-1 overflow-x-auto py-2.5 scrollbar-hide">
            {stickyNavLinks.map((link) => (
              <a key={link.href} href={link.href} className="text-xs font-semibold text-[#1A365D] hover:text-[#F6C344] hover:bg-[#EEF2F8] px-3 py-1.5 rounded-md whitespace-nowrap transition-all">
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Processing Times */}
      <section id="processing" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Passport Processing</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Oklahoma City Passport Processing Times</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base">Choose the speed that fits your travel timeline. From same-day emergency to budget-friendly 2-week service.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { label: "Emergency", time: "24 Hours", price: "From $499", color: "bg-red-50 border-red-200", badge: "Life-or-death only" },
              { label: "Rush", time: "2–3 Days", price: "From $399", color: "bg-orange-50 border-orange-200", badge: "Most popular" },
              { label: "Expedited", time: "5–7 Days", price: "From $249", color: "bg-[#F6C344]/10 border-[#F6C344]/30", badge: "Best value" },
              { label: "Standard", time: "10–14 Days", price: "From $149", color: "bg-green-50 border-green-200", badge: "Budget friendly" },
            ].map((card, i) => (
              <div key={i} className={`rounded-lg border p-6 ${card.color}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-bold text-[#1A365D]">{card.label}</span>
                  <span className="text-[10px] font-semibold bg-white px-2 py-0.5 rounded-full text-gray-600">{card.badge}</span>
                </div>
                <p className="text-3xl font-bold text-[#1A365D] mb-1">{card.time}</p>
                <p className="text-sm text-gray-600 mb-4">{card.price}</p>
                <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-1 text-sm font-semibold text-[#1A365D] hover:text-[#F6C344] whitespace-nowrap">
                  Get Started <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-16 md:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">What We Offer</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services in Oklahoma City</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base">Comprehensive passport solutions for every situation — from first-time applications to emergency replacements.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((s, i) => (
              <div key={i} className="bg-white rounded-lg p-6 border border-gray-100 hover:border-[#F6C344]/40 transition-all hover:shadow-sm">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#EEF2F8] mb-4">
                  <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-base mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Area */}
      <section id="local-area" className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">About the Area</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services in Oklahoma City &amp; Surrounding Communities</h2>
          </div>
          <div className="prose prose-lg max-w-none text-gray-600 leading-relaxed">
            <p className="mb-4">
              Oklahoma City is the capital and largest city of Oklahoma, home to over 680,000 residents and the heart of the state's economic and cultural life. As the seat of Oklahoma County and the anchor of the OKC Metro area, the city serves a diverse population with unique passport needs that vary by neighborhood, profession, and lifestyle.
            </p>
            <p className="mb-4">
              <strong className="text-[#1A365D]">Downtown &amp; Bricktown:</strong> The central business district is home to Oklahoma's energy industry headquarters, including Devon Energy, Chesapeake Energy, and numerous oil and gas firms. Business travelers from downtown OKC frequently need expedited passport renewals for international trade missions, conferences in Dubai, London, and Tokyo, and corporate retreats. The proximity to Will Rogers World Airport makes last-minute travel common — and passport emergencies frequent.
            </p>
            <p className="mb-4">
              <strong className="text-[#1A365D]">Tinker Air Force Base &amp; Midwest City:</strong> Tinker AFB is the largest military installation in Oklahoma with over 26,000 military and civilian personnel. Military families receive PCS orders with short notice, often requiring passport renewals for dependents before OCONUS assignments. The base passport office serves active duty but has limited hours and capacity. Our expedited service helps Tinker families meet tight timelines for moves to Germany, Japan, South Korea, and the UK.
            </p>
            <p className="mb-4">
              <strong className="text-[#1A365D]">University of Oklahoma &amp; Norman:</strong> Just 20 miles south of OKC, the University of Oklahoma sends thousands of students abroad each year through study programs, research exchanges, and athletic competitions. The Norman community also includes OU Health medical professionals attending international conferences. Students and faculty often discover passport issues 2–3 weeks before departure, making expedited service essential.
            </p>
            <p className="mb-4">
              <strong className="text-[#1A365D]">Edmond &amp; North OKC:</strong> Edmond is one of Oklahoma's fastest-growing suburbs, home to the University of Central Oklahoma and affluent families in neighborhoods like Oak Tree, Coffee Creek, and Valencia. Edmond residents travel frequently for leisure — Europe in summer, Caribbean cruises in spring, and ski trips to Canada in winter. The city's proximity to OKC means residents can easily access acceptance facilities while using our expedited processing.
            </p>
            <p className="mb-4">
              <strong className="text-[#1A365D]">Moore &amp; South OKC:</strong> The southern suburbs serve working families and young professionals who travel for both business and pleasure. Moore residents often need budget-friendly expedited service for family vacations to Mexico, mission trips to Central America, and spring break travel with children.
            </p>
            <p className="mb-4">
              <strong className="text-[#1A365D]">Yukon, Mustang &amp; Bethany:</strong> These western suburbs have growing populations with increasing international travel needs. Many residents commute to downtown OKC for work and need passport services that fit their schedules without requiring multiple office visits.
            </p>
            <p>
              Whether you are a Tinker Air Force Base family with PCS orders, a downtown executive with a business trip to Asia, a student at OCCC or UCO, or a family in Edmond planning a European vacation, we provide Oklahoma City passport services tailored to your timeline and budget. Our registered courier service eliminates the uncertainty of mailing and delivers directly to the State Department — giving you the fastest processing available.
            </p>
          </div>
        </div>
      </section>

      {/* Travel Timing */}
      <section id="timing" className="py-16 md:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">When to Apply</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Best Times to Get a Passport in Oklahoma City</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base">Plan around Oklahoma's travel seasons and peak demand periods.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {timingCards.map((card, i) => (
              <div key={i} className="bg-white rounded-lg p-6 border border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#EEF2F8] mb-4">
                  <i className="ri-calendar-event-line text-[#1A365D] text-lg"></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-base mb-1">{card.title}</h3>
                <p className="text-[#F6C344] text-xs font-semibold mb-3">{card.months}</p>
                <p className="text-gray-500 text-sm leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Destinations */}
      <section id="destinations" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Oklahoma City Residents Travel</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base">Common destinations with passport requirements for OKC travelers.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {destinations.map((d, i) => (
              <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">{d.flag}</span>
                  <div>
                    <h3 className="font-bold text-[#1A365D] text-base">{d.name}</h3>
                    <p className="text-gray-500 text-xs">{d.country}</p>
                  </div>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Annual Passports Issued", value: "~4,500", sub: "Oklahoma City metro" },
              { label: "Peak Season", value: "May–Aug", sub: "Family vacation rush" },
              { label: "Top Destination", value: "Mexico", sub: "Cancun & Cabo San Lucas" },
              { label: "Military Passports", value: "~800/yr", sub: "Tinker AFB families" },
            ].map((stat, i) => (
              <div key={i} className="text-center bg-[#EEF2F8] rounded-lg p-5">
                <p className="text-2xl font-bold text-[#1A365D]">{stat.value}</p>
                <p className="text-sm font-semibold text-gray-700 mt-1">{stat.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{stat.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-16 md:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Oklahoma City Residents Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-lg p-6 border border-gray-100">
                <div className="flex items-center gap-0.5 mb-4">
                  {[...Array(5)].map((_, j) => <i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>)}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#EEF2F8] flex items-center justify-center text-sm font-bold text-[#1A365D]">{t.name[0]}</div>
                  <div>
                    <p className="text-sm font-semibold text-[#1A365D]">{t.name}</p>
                    <p className="text-xs text-gray-500">{t.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Acceptance Facilities */}
      <section id="facilities" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Offices</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Acceptance Facilities in Oklahoma City</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base">Visit these locations for identity verification before we expedite your passport.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {facilities.map((f, i) => (
              <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-100">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#EEF2F8] flex-shrink-0">
                    <i className="ri-map-pin-line text-[#1A365D]"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1A365D] text-base">{f.name}</h3>
                    <p className="text-sm text-gray-500">{f.address}</p>
                  </div>
                </div>
                <div className="space-y-1.5 text-sm text-gray-600 ml-12">
                  <p><i className="ri-time-line text-[#F6C344] mr-2"></i>{f.hours}</p>
                  <p><i className="ri-phone-line text-[#F6C344] mr-2"></i>{f.phone}</p>
                  <p><i className="ri-information-line text-[#F6C344] mr-2"></i>{f.notes}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" className="py-16 md:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare Options</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Oklahoma City Passport Service Comparison</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-[#1A365D] text-white">
                  <th className="text-left p-3 font-semibold rounded-tl-lg">Feature</th>
                  <th className="text-center p-3 font-semibold">Fast Passport Center</th>
                  <th className="text-center p-3 font-semibold">US Dept. of State</th>
                  <th className="text-center p-3 font-semibold">FedEx</th>
                  <th className="text-center p-3 font-semibold">Staples</th>
                  <th className="text-center p-3 font-semibold rounded-tr-lg">Local Expediters</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F7F8FA]"}>
                    <td className="p-3 font-semibold text-[#1A365D]">{row.feature}</td>
                    <td className="p-3 text-center">
                      <span className="text-[#1A365D] font-semibold">{row.us}</span>
                      <p className="text-[#F6C344] text-xs mt-1">{row.diff}</p>
                    </td>
                    <td className="p-3 text-center text-gray-500">{row.usdps}</td>
                    <td className="p-3 text-center text-gray-500">{row.fedex}</td>
                    <td className="p-3 text-center text-gray-500">{row.staples}</td>
                    <td className="p-3 text-center text-gray-500">{row.local}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Mistakes */}
      <section id="mistakes" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Passport Mistakes in Oklahoma City</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {mistakes.map((m, i) => (
              <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-100">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 flex-shrink-0">
                    <i className="ri-error-warning-line text-red-500 text-sm"></i>
                  </div>
                  <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 md:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">The Process</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Oklahoma City Residents</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
            {steps.map((step, i) => (
              <div key={i} className="relative">
                <div className="bg-white rounded-lg p-6 border border-gray-100 h-full">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F6C344] text-[#1A365D] font-bold text-lg mb-4">{step.num}</div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-2">{step.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                </div>
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 -right-2.5 z-10">
                    <i className="ri-arrow-right-line text-[#F6C344] text-xl"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Common Questions</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Oklahoma City Passport FAQ</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-[#F7F8FA] rounded-lg border border-gray-100 overflow-hidden">
                <details className="group">
                  <summary className="flex items-center justify-between p-5 cursor-pointer">
                    <span className="font-semibold text-[#1A365D] text-sm pr-4">{faq.q}</span>
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0">
                      <i className="ri-add-line text-[#1A365D] group-open:hidden"></i>
                      <i className="ri-subtract-line text-[#1A365D] hidden group-open:block"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-5 text-gray-500 text-sm leading-relaxed">{faq.a}</div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Helpful Resources */}
      <section id="resources" className="py-16 md:py-24 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Useful Links</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Oklahoma City Travelers</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {resources.map((r, i) => (
              <a key={i} href={r.url} target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-100 hover:border-[#F6C344]/40 transition-all group">
                <h3 className="font-bold text-[#1A365D] text-sm mb-2 group-hover:text-[#F6C344] transition-colors">{r.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{r.desc}</p>
                <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-[#1A365D] group-hover:text-[#F6C344]">
                  Visit <i className="ri-arrow-right-up-line"></i>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Cross Links */}
      <SEOCrossLinks currentRoute="/cities/oklahoma-city" state="OK" />

      {/* Final CTA */}
      <section id="cta" className="py-20 md:py-28 bg-[#1A365D]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-5">Ready to Get Your Passport Fast?</h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            Oklahoma City's trusted expedited passport service. Whether you are in Bricktown, Midwest City, Edmond, or Moore — we have you covered. Call now or start online.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
              Start My Passport <i className="ri-arrow-right-line"></i>
            </a>
            <a href="tel:8772530084" className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-base px-8 py-4 rounded-md transition-all whitespace-nowrap">
              <i className="ri-phone-line"></i> 877-253-0084
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}